import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Autocomplete,
  Collapse,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  Receipt as ReceiptIcon,
  Visibility,
  VisibilityOff,
  LocationOn,
} from "@mui/icons-material";
import {
  uniqueTruckNumbers,
  freightsByTruck,
  partyList,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebounce } from "../../../Hooks/useDebounce";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
  "& .MuiAutocomplete-inputRoot": { height: 34, ...sq, bgcolor: T.primaryBg },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1, sm: 1.5 },
      textAlign: "center",
      ...sq,
      border: `1.5px solid ${color}40`,
      borderTop: `3px solid ${color}`,
      bgcolor: `${color}08`,
      boxShadow: "none",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.55rem", sm: "0.65rem" },
        color: T.textSub,
        lineHeight: 1.2,
        mb: 0.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.72rem", sm: "1rem" },
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
      title={value}
    >
      {value}
    </Typography>
  </Paper>
);

// ── LabelValue ─────────────────────────────────────────────────────
const LabelValue = ({ label, value, color }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.58rem",
        color: T.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
    >
      {value}
    </Typography>
  </Box>
);

// ── MobileCard ─────────────────────────────────────────────────────
const MobileCard = ({ row, idx, expanded, onToggle }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 1,
      ...sq,
      border: `1.5px solid ${expanded ? T.primary : T.border}`,
      bgcolor: T.white,
      overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
    }}
  >
    {/* Header Row */}
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
      {/* Icon */}
      <Box
        sx={{
          width: 58,
          flexShrink: 0,
          bgcolor: expanded ? T.primary : T.primaryBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
          transition: "background-color 0.2s",
          gap: 0.25,
        }}
      >
        <TruckIcon
          sx={{ fontSize: 20, color: expanded ? T.white : T.primary }}
        />
        <Typography
          sx={{
            fontSize: "0.46rem",
            fontWeight: 700,
            color: expanded ? T.white : T.primary,
          }}
        >
          #{idx + 1}
        </Typography>
      </Box>

      {/* Main info */}
      <Box
        sx={{
          flex: 1,
          px: 1.2,
          py: 0.75,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.82rem",
            color: T.text,
            lineHeight: 1.2,
            mb: 0.15,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.truck_no || "—"}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.62rem",
            color: T.textSub,
            fontWeight: 500,
            mb: 0.15,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.party_name || "—"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LocationOn sx={{ fontSize: 10, color: T.textMuted }} />
          <Typography
            sx={{
              fontSize: "0.58rem",
              color: T.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.from_location || "—"} → {row.to_location || "—"}
          </Typography>
        </Box>
      </Box>

      {/* Amount + toggle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          px: 1,
          py: 0.75,
          minWidth: 100,
        }}
      >
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{ fontSize: "0.48rem", color: T.textMuted, fontWeight: 600 }}
          >
            Net Payable
          </Typography>
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.8rem", color: T.red }}
          >
            ₹{row.net_payable || 0}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={onToggle}
          startIcon={
            expanded ? (
              <VisibilityOff sx={{ fontSize: 12 }} />
            ) : (
              <Visibility sx={{ fontSize: 12 }} />
            )
          }
          sx={{
            ...ghostBtn,
            height: 22,
            fontSize: "0.55rem",
            px: 0.7,
            "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
          }}
        >
          {expanded ? "Hide" : "View"}
        </Button>
      </Box>
    </Box>

    {/* Expanded detail */}
    {expanded && (
      <Box
        sx={{
          bgcolor: T.primaryBg,
          borderTop: `1.5px solid ${T.border}`,
          p: 1.25,
        }}
      >
        <Stack spacing={0.75}>
          {/* Basic Info */}
          <Grid container spacing={0.5}>
            {[
              ["ID", row.freight_id],
              ["Loading", formatDate(row.loading_date)],
              ["Unloading", formatDate(row.unloading_date)],
            ].map(([l, v]) => (
              <Grid item xs={6} key={l}>
                <LabelValue label={l} value={v} />
              </Grid>
            ))}
          </Grid>

          {/* Weight */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.white,
              border: `1px solid ${T.border}`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.primary,
                mb: 0.4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              ⚖️ Weight
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["Load", row.loading_wt || 0, null],
                ["Recv", row.received_wt || 0, null],
                ["Short", row.shortage_wt || 0, T.red],
                ["Bill", row.billing_wt || 0, null],
              ].map(([l, v, c]) => (
                <Grid item xs={3} key={l}>
                  <LabelValue label={l} value={v} color={c} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Charges */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.white,
              border: `1px solid ${T.border}`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.primary,
                mb: 0.4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              💰 Charges
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["Rate", `₹${row.freight_rate || 0}`],
                ["LR Amt", `₹${row.freight_amt || 0}`],
                ["TDS", `₹${row.tds_amt || 0}`],
                ["Shortage", `₹${row.shortage_amt || 0}`],
                ["Commission", `₹${row.commission || 0}`],
                ["Other", `₹${row.other_exp || 0}`],
              ].map(([l, v]) => (
                <Grid item xs={6} key={l}>
                  <LabelValue label={l} value={v} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Advance */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.warningBg,
              border: `1px solid ${T.warning}30`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.warning,
                mb: 0.4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              💵 Advance
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["Mines", `₹${row.ad_mines_exp || 0}`],
                ["Diesel", `₹${row.ad_diesel_exp || 0}`],
                ["Cash", `₹${row.ad_cash_exp || 0}`],
              ].map(([l, v]) => (
                <Grid item xs={4} key={l}>
                  <LabelValue label={l} value={v} color={T.warning} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Summary */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.redBg,
              border: `1.5px solid ${T.redBorder}`,
              ...sq,
            }}
          >
            <Grid container spacing={0.5}>
              {[
                ["Net Payable", `₹${row.net_payable || 0}`, T.primary],
                ["Balance", `₹${row.bal_amount?.toFixed(1) || 0}`, T.red],
                ["Paid", `₹${row.paid_amount?.toFixed(1) || 0}`, T.success],
                ["Net Amount", `₹${row.net_amount || 0}`, null],
              ].map(([l, v, c]) => (
                <Grid item xs={6} key={l}>
                  <LabelValue label={l} value={v} color={c} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Remarks */}
          {row.remark && (
            <Box>
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  color: T.textMuted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Remark
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: T.text, fontWeight: 500 }}
              >
                {row.remark}
              </Typography>
            </Box>
          )}
          {row.payment_remark && (
            <Box>
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  color: T.textMuted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Payment Remark
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: T.text, fontWeight: 500 }}
              >
                {row.payment_remark}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    )}
  </Paper>
);

// ── Table Header Config ────────────────────────────────────────────
// ── Table Header Config ────────────────────────────────────────────
const TH = [
  { l: "#", w: 35, sticky: true, left: 0 },
  { l: "LR No", w: 90, sticky: true, left: 35 },
  { l: "Truck No", w: 110, sticky: true, left: 125 },
  { l: "Party", w: 140 },
  { l: "Loading Date", w: 100 },
  { l: "Loading Out", w: 100 },
  { l: "Unloading Date", w: 110 },
  { l: "Unloading Out", w: 110 },
  { l: "From", w: 120 },
  { l: "To", w: 120 },
  { l: "E-way Bill No", w: 120 },
  { l: "E-way Bill Date", w: 110 },
  { l: "E-way Validity", w: 120 },
  { l: "Invoice No", w: 100 },
  { l: "Classification", w: 150 },
  { l: "Packing Method", w: 150 },
  { l: "Truck Owner", w: 150 },
  { l: "Phone", w: 110 },
  { l: "Ref No", w: 90 },
  { l: "Remark", w: 140 },
  { l: "Additional Info", w: 160 },
  { l: "Date", w: 100 },
];
const COLSPAN = TH.length;

// ── Main Component ─────────────────────────────────────────────────
export default function Vehiclefreight() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [truckOptions, setTruckOptions] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [lrFilter, setLrFilter] = useState("");
  const [partyFilter, setPartyFilter] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [partyOptions, setParties] = useState([]);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const debouncedLrFilter = useDebounce(lrFilter, 500);
  const debouncedPartyFilter = useDebounce(partyFilter, 500);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setParties(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setParties([]);
      }
    };
    fetchParties();
  }, []);

  const fetchFreights = useCallback(
    async (truckNo, pageNo = 1, append = false) => {
      if (!truckNo) return;
      setLoading(true);
      try {
        const res = await freightsByTruck(truckNo, pageNo, {
          lr_number: debouncedLrFilter || undefined,
          party_name: debouncedPartyFilter || undefined,
        });

        const results = res?.results || [];
        setData((p) => (append ? [...p, ...results] : results));
        setHasMore(!!res?.next);
        setPage(pageNo + 1);
      } catch (err) {
        console.error("Fetch error:", err);
        setData([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [debouncedLrFilter, debouncedPartyFilter],
  );

  useEffect(() => {
    const fetchTrucks = async () => {
      try {
        const res = await uniqueTruckNumbers();
        setTruckOptions(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch trucks:", error);
        setTruckOptions([]);
      }
    };
    fetchTrucks();
  }, []);

  useEffect(() => {
    if (!selectedTruck) return;

    setData([]);
    setPage(1);
    setHasMore(true);
    setExpandedCardId(null);
    fetchFreights(selectedTruck, 1, false);
  }, [debouncedLrFilter, debouncedPartyFilter, selectedTruck, fetchFreights]);

  const fetchMoreData = () => {
    if (!selectedTruck || !hasMore || loading) return;
    fetchFreights(selectedTruck, page, true);
  };

  const handleTruckChange = (t) => {
    setSelectedTruck(t);
    setData([]);
    setPage(1);
    setHasMore(true);
    setExpandedCardId(null);
  };

  const handleSearch = () => {
    if (!selectedTruck) return;
    setData([]);
    setPage(1);
    setHasMore(true);
    fetchFreights(selectedTruck, 1, false);
    setExpandedCardId(null);
  };

  const clearFilters = () => {
    setLrFilter("");
    setPartyFilter("");
  };

  // ── Totals ─────────────────────────────────────────────────────
  const sumFix = (key, dec = 1) =>
    data.reduce((s, c) => s + (+c[key] || 0), 0).toFixed(dec);
  const tots = {
    loading_wt: sumFix("loading_wt", 3),
    received_wt: sumFix("received_wt", 3),
    shortage_wt: sumFix("shortage_wt", 3),
    billing_wt: sumFix("billing_wt", 3),
    freight_amt: sumFix("freight_amt"),
    tds_amt: sumFix("tds_amt"),
    shortage_amt: sumFix("shortage_amt"),
    total_advance: sumFix("total_advance"),
    commission: sumFix("commission"),
    net_amount: sumFix("net_amount"),
    other_exp: sumFix("other_exp"),
    net_payable: sumFix("net_payable"),
    paid_amount: sumFix("paid_amount"),
    bal_amount: sumFix("bal_amount"),
  };

  // ── Export ─────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");

    const headers = [
      "Sr No",
      "LR ID",
      "Party",
      "Truck",
      "Loading",
      "Net Payable",
    ];

    const rows = data.map((r, i) => [
      i + 1,
      r.freight_id ?? "—",
      r.party_name || "—",
      r.truck_no || "—",
      formatDate(r.loading_date),
      r.net_payable ?? "—",
    ]);

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));
      return { wch: maxLen + 4 };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Truck LR's",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "Truck LR's");
    XLSX.writeFile(
      wb,
      `Truck_Freights_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Truck LR's</title>
      <style>body{font-family:Arial;padding:20px}h2,h4{text-align:center}
      table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px}
      th{background:#f5f5f5}</style></head>
      <body><h2>PARAS ROAD LINES</h2><h4>Truck LR's - ${selectedTruck || ""}</h4>
      <table><thead><tr><th>S.No</th><th>ID</th><th>Party</th><th>Loading</th>
      <th>From</th><th>To</th><th>Net Pay</th></tr></thead>
      <tbody>${data
        .map(
          (r, i) => `<tr><td>${i + 1}</td><td>${r.freight_id || "—"}</td>
      <td>${r.party_name || "—"}</td><td>${formatDate(r.loading_date)}</td>
      <td>${r.from_location || "—"}</td><td>${r.to_location || "—"}</td>
      <td>₹${r.net_payable || 0}</td></tr>`,
        )
        .join("")}
      </tbody></table></body></html>`);
    w.document.close();
    w.print();
    w.close();
  };

  // ── Empty state ───────────────────────────────────────────────
  const EmptyState = ({ message }) => (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        border: `1.5px dashed ${T.border}`,
        bgcolor: T.primaryBg,
        ...sq,
      }}
    >
      <TruckIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}>
        {message}
      </Typography>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -4, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stat Cards ── */}
      <Box sx={{ mt: 2, mb: 1.5, width: "100%" }}>
        <Grid
          container
          spacing={0.75}
          sx={{
            mx: 0,
            width: "100%",
            "& > .MuiGrid-item": { px: "3px !important", py: "3px !important" },
          }}
        >
          {[
            { title: "Total Records", value: data.length, color: T.primary },
            {
              title: "Net Payable",
              value: formatCurrency(tots.net_payable),
              color: T.red,
            },
            {
              title: "Paid Amount",
              value: formatCurrency(tots.paid_amount),
              color: T.success,
            },
            {
              title: "Balance Due",
              value: formatCurrency(tots.bal_amount),
              color: T.warning,
            },
          ].map((s) => (
            <Grid item xs={6} sm={3} key={s.title}>
              <StatCard {...s} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* Truck Select */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={truckOptions}
              getOptionLabel={(o) => o?.truck_no || o || ""}
              value={selectedTruck}
              onChange={(_, v) => handleTruckChange(v?.truck_no || v || null)}
              size="small"
              renderInput={(p) => (
                <TextField
                  {...p}
                  placeholder="Select Truck"
                  InputProps={{
                    ...p.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <TruckIcon sx={{ fontSize: 14, color: T.primary }} />
                        </InputAdornment>
                        {p.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* LR Filter */}
          <Grid item xs={6} sm={3} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search LR No..."
              value={lrFilter}
              onChange={(e) => setLrFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon sx={{ fontSize: 13, color: T.textMuted }} />
                  </InputAdornment>
                ),
                endAdornment: lrFilter !== debouncedLrFilter && (
                  <InputAdornment position="end">
                    <CircularProgress size={12} sx={{ color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Party Filter */}
          <Grid item xs={6} sm={3} md={3}>
            <Autocomplete
              freeSolo
              options={partyOptions.map((o) => o.name)}
              value={partyFilter}
              onInputChange={(_, v) => setPartyFilter(v || "")}
              size="small"
              renderInput={(p) => (
                <TextField
                  {...p}
                  placeholder="Search Party..."
                  InputProps={{
                    ...p.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{ fontSize: 13, color: T.textMuted }}
                          />
                        </InputAdornment>
                        {p.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {partyFilter !== debouncedPartyFilter && (
                          <InputAdornment position="end">
                            <CircularProgress
                              size={12}
                              sx={{ color: T.primary }}
                            />
                          </InputAdornment>
                        )}
                        {p.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Search */}
          <Grid item xs="auto">
            <Tooltip title="Search" arrow>
              <Button
                size="small"
                onClick={handleSearch}
                disabled={loading}
                sx={{ ...primaryBtn, height: 34, minWidth: 34, px: 1 }}
              >
                {loading ? (
                  <CircularProgress size={14} sx={{ color: T.white }} />
                ) : (
                  <SearchIcon sx={{ fontSize: 15 }} />
                )}
              </Button>
            </Tooltip>
          </Grid>

          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Export */}
          <Grid item xs="auto">
            <Tooltip title="Export Excel" arrow>
              <Button
                size="small"
                onClick={handleExportExcel}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <DownloadIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          {/* Print */}
          <Grid item xs="auto">
            <Tooltip title="Print" arrow>
              <Button
                size="small"
                onClick={handlePrint}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <PrintIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ── Mobile View ── */}
      {isMobile ? (
        <Box>
          {loading && data.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : !selectedTruck ? (
            <EmptyState message="Please select a truck to view records" />
          ) : data.length === 0 ? (
            <EmptyState message="No records found" />
          ) : (
            <Box
              id="truck-mobile"
              sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={data.length}
                next={fetchMoreData}
                hasMore={hasMore && !!selectedTruck}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="truck-mobile"
              >
                {data.map((r, i) => (
                  <MobileCard
                    key={r.freight_id || i}
                    row={r}
                    idx={i}
                    expanded={expandedCardId === (r.freight_id || i)}
                    onToggle={() => handleCardToggle(r.freight_id || i)}
                  />
                ))}
              </InfiniteScroll>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop View ── */
        <>
          {/* Main Table */}
          <Paper
            elevation={0}
            sx={{
              ...sq,
              border: `1.5px solid ${T.border}`,
              overflow: "hidden",
              boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
              height: "calc(100vh - 230px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableContainer
              id="scrollableDiv"
              sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 2800,
                  borderCollapse: "collapse",
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.75rem",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  },
                  "& td": { borderBottom: `1px solid #ccc`, height: 26 },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    height: 28,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {TH.map(({ l, w, sticky, left }) => (
                      <TableCell
                        key={l}
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                          ...(sticky && {
                            position: "sticky",
                            left,
                            zIndex: 12,
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {/* ── Empty / Loading States ── */}
                  {!selectedTruck ? (
                    <TableRow>
                      <TableCell
                        colSpan={COLSPAN}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          border: "none !important", // ← force no border
                        }}
                      >
                        <TruckIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          Please select a truck to view records
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : loading && data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={COLSPAN}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          border: "none !important", // ← force no border
                        }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={COLSPAN}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          border: "none !important", // ← force no border
                        }}
                      >
                        <TruckIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>

              {/* ── InfiniteScroll ONLY when data exists ── */}
              {data.length > 0 && (
                <InfiniteScroll
                  dataLength={data.length}
                  next={fetchMoreData}
                  hasMore={hasMore && !!selectedTruck}
                  loader={
                    <Box sx={{ textAlign: "center", py: 1 }}>
                      <CircularProgress size={20} sx={{ color: T.primary }} />
                    </Box>
                  }
                  scrollableTarget="scrollableDiv"
                  style={{ overflow: "visible" }}
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth: 2800,
                      borderCollapse: "collapse",
                      "& td": {
                        py: 0,
                        px: 0.75,
                        fontSize: "0.75rem",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        lineHeight: 1.2,
                        borderBottom: `1px solid #ccc`,
                        height: 26,
                      },
                    }}
                  >
                    <TableBody>
                      {data.map((row, idx) => {
                        const isEven = idx % 2 === 0;
                        const rowBg = isEven ? T.baseRow : T.altRow;
                        const sc = {
                          bgcolor: `${rowBg} !important`,
                          zIndex: 5,
                        };

                        return (
                          <TableRow
                            key={row.freight_id || idx}
                            sx={{
                              "& > td": {
                                bgcolor: rowBg,
                                transition: "background-color 0.12s",
                              },
                              "&:hover > td": {
                                bgcolor: `${T.rowHover} !important`,
                              },
                            }}
                          >
                            {/* ── # ── sticky left 0 */}
                            <TableCell
                              sx={{ position: "sticky", left: 0, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  color: T.textMuted,
                                }}
                              >
                                {idx + 1}
                              </Typography>
                            </TableCell>

                            {/* ── LR No ── sticky left 35 */}
                            <TableCell
                              sx={{ position: "sticky", left: 35, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: T.text,
                                }}
                              >
                                {row.lr_number || "—"}
                              </Typography>
                            </TableCell>

                            {/* ── Truck No ── sticky left 125 (35 + 90) */}
                            <TableCell
                              sx={{ position: "sticky", left: 125, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: T.primary,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {row.truck_no || "—"}
                              </Typography>
                            </TableCell>

                            {/* ── Party ── (not sticky, continues normally) */}
                            <TableCell>
                              <Tooltip title={row.party_name || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 140,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.party_name || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.loading_date)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.loading_out_date)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.unloading_date)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.unloading_out_date)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Tooltip title={row.from_location || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.from_location || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Tooltip title={row.to_location || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.to_location || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.eway_bill_no || "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.eway_bill_date)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.eway_validity_date)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.invoice_no || "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Tooltip
                                title={row.classification_of_goods || ""}
                                arrow
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 140,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.classification_of_goods || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Tooltip
                                title={row.method_of_packing || ""}
                                arrow
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 140,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.method_of_packing || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Tooltip title={row.truck_owner || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 140,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.truck_owner || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.phone_no || "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.tp_no || "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Tooltip title={row.remark || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 130,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.remark || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Tooltip title={row.additional_info || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 150,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.additional_info || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={formatDate(row.created_at)}
                                size="small"
                                sx={{
                                  fontSize: "0.6rem",
                                  height: 16,
                                  ...sq,
                                  bgcolor: T.primaryBg,
                                  color: T.primary,
                                  border: `1px solid ${T.border}`,
                                  fontWeight: 700,
                                  "& .MuiChip-label": { px: 0.75 },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </InfiniteScroll>
              )}
            </TableContainer>
          </Paper>
        </>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "80px !important",
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ ...sq, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
